import { useState } from 'react'
import { isEmpty } from 'remeda'
import { Navigation, type Swiper, type SwiperOptions } from 'swiper'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'
import { ChevronRightIcon } from '@velasca/icons/chevronRight'
import type { ApparelProductProps, Variant } from '@velasca/types'
import {
  SwatchImg,
  SwiperDesktopNextButton,
  VariantButton,
  VariantSelectedHighlight,
  VariantsSliderWrapper,
} from './VariantsSlider.styles'

export const VariantsSlider = ({
  title = '',
  variants = [],
  setSelectedVariant = undefined,
  selectedVariant,
  slidesPerView = 4.25,
  spaceBetween = 3,
  breakpoints = {},
}: {
  title: string
  variants: ApparelProductProps['variants']
  setSelectedVariant?: ((variant: Variant) => void) | undefined
  selectedVariant: Variant
  slidesPerView?: number
  spaceBetween?: number
  breakpoints?: {
    [width: number]: SwiperOptions
    [ratio: string]: SwiperOptions
  }
}) => {
  const [slider, setSlider] = useState<Swiper | undefined>()

  if (!variants || (variants && isEmpty(variants))) {
    return
  }

  const showNextDesktopButton = (slider && !slider.isEnd) || false

  return (
    // Use the variant-slider-wrapper class to override the default styles in PDP
    <VariantsSliderWrapper className="variant-slider-wrapper">
      <SwiperComponent
        modules={[Navigation]}
        scrollbar={{ draggable: true }}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        breakpoints={breakpoints}
        onSwiper={setSlider}
      >
        {variants
          .map((variant) => {
            const frontImageUrl = variant.img?.swatch

            if (!frontImageUrl) {
              return
            }

            // TODO: due to the EEA, aria labels and alt has been added in en only. Define how to handle this in the future for a EEA assessment and complete implementation
            return (
              <SwiperSlide key={variant.id}>
                <VariantButton
                  aria-label={`Select variant ${title} with ${variant.description?.bold || ''} ${variant.description?.thin || ''} to see more details`}
                  {...(setSelectedVariant
                    ? {
                        onClick: () => {
                          setSelectedVariant(variant)
                        },
                      }
                    : {
                        as: 'a',
                        href: `/products/${variant.handle}`,
                      })}
                >
                  <SwatchImg
                    loading="lazy"
                    key={variant.id}
                    src={frontImageUrl}
                    alt={`Alternative version of ${title} with ${variant.description?.bold || ''} ${variant.description?.thin || ''}`}
                  />
                  {selectedVariant.id === variant.id && (
                    <VariantSelectedHighlight />
                  )}
                </VariantButton>
              </SwiperSlide>
            )
          })
          .filter((el) => el !== undefined)}
      </SwiperComponent>
      {showNextDesktopButton && (
        <SwiperDesktopNextButton
          onClick={() => {
            if (!slider) {
              return
            }

            // If the slider is at the end, go back to the start
            if (slider.isEnd) {
              slider.slideTo(0)

              return
            }

            slider.slideNext()
          }}
        >
          <ChevronRightIcon />
        </SwiperDesktopNextButton>
      )}
    </VariantsSliderWrapper>
  )
}
